import {computed, inject, Injectable, signal} from '@angular/core';
import {firstValueFrom, of} from 'rxjs';
import {catchError, finalize, map, tap} from 'rxjs/operators';
import {StorageService} from '../../storage/storage.service';
import {UserService} from '../user.service';
import {CurrentUser} from '../../../models/current-user.model';

@Injectable({
  providedIn: 'root',
})
export class CurrentUserStore {

  private userState = signal<CurrentUser | null>(null);
  private initializedState = signal(false);
  private loadingState = signal(false);
  private initPromise: Promise<void> | null = null;

  readonly user = computed(() => this.userState());
  readonly roles = computed(() => this.userState()?.roles ?? []);
  readonly permissions = computed(() => this.userState()?.permissions ?? []);
  readonly initialized = computed(() => this.initializedState());
  readonly loading = computed(() => this.loadingState());
  readonly authenticated = computed(() => !!this.userState());

  private readonly userService = inject(UserService);
  private readonly storageService = inject(StorageService);

  initialize(force = false): Promise<void> {
    if (this.initPromise) return this.initPromise;

    if (!this.storageService.getAccessToken()) {
      this.clear();
      return Promise.resolve();
    }

    if (this.initializedState() && !force) {
      return Promise.resolve();
    }

    this.loadingState.set(true);

    this.initPromise = firstValueFrom(
      this.userService.getCurrentUser().pipe(
        map((response: any) => response?.data ?? null),
        tap((user) => this.setUser(user)),
        catchError(() => {
          this.storageService.clearStorage();
          this.clear();
          return of(null);
        }),
        finalize(() => {
          this.loadingState.set(false);
          this.initializedState.set(true);
          this.initPromise = null;
        }),
        map(() => void 0),
      )
    );

    return this.initPromise;
  }

  refresh(): Promise<void> {
    this.initializedState.set(false);
    return this.initialize(true);
  }

  clear(): void {
    this.userState.set(null);
    this.initializedState.set(true);
    this.loadingState.set(false);
    this.initPromise = null;
  }

  hasRole(role: string): boolean {
    return this.roles().includes(role);
  }

  hasAnyRole(roles: string[]): boolean {
    return roles.some((role) => this.hasRole(role));
  }

  hasPermission(permission: string): boolean {
    return this.permissions().includes(permission);
  }

  hasAnyPermission(permissions: string[]): boolean {
    return permissions.some((permission) => this.hasPermission(permission));
  }

  hasAllPermissions(permissions: string[]): boolean {
    return permissions.every((permission) => this.hasPermission(permission));
  }

  private setUser(user: CurrentUser | null): void {
    if (!user) {
      this.userState.set(null);
      return;
    }

    this.userState.set({
      ...user,
      roles: Array.from(new Set(user.roles ?? [])),
      permissions: Array.from(new Set(user.permissions ?? [])),
    });
  }
}
